import { loadJS } from '@ahm/common-helpers';
import { useAuth } from '@ahm/react-auth';
import { useCallback, useMemo, useState } from 'react';
import { publicEnv } from '@/config/public-env.config.mjs';

declare global {
  interface Window {
    fcWidget?: {
      init: (config: {
        token: string;
        host: string;
        headerProperty?: {
          hideChatButton?: boolean;
        };
        tags?: string[];
        siteId?: string;
      }) => void;
      open: () => void;
      close: () => void;
      user: {
        setProperties: (properties: {
          externalId?: string;
          firstName?: string;
          email?: string;
          phone?: string;
          orderId?: string;
        }) => void;
      };
    };
  }
}

export interface FreshchatConfig {
  hideChatButton?: boolean;
  tags?: string[];
  siteId?: string;
  externalData?: {
    orderId?: string;
  };
}

export interface UseFreshchatReturn {
  isAvailable: boolean;
  openChat: () => void;
  closeChat: () => void;
  isLoading: boolean;
}

export function useFreshchat(config?: FreshchatConfig): UseFreshchatReturn {
  const { session } = useAuth();
  const [isLoading, setIsLoading] = useState(false);

  const isAvailable = useMemo(() => {
    return !!(
      publicEnv.NEXT_PUBLIC_FRESH_CHAT_HOST &&
      publicEnv.NEXT_PUBLIC_FRESH_CHAT_TOKEN
    );
  }, []);

  const initializeFreshChat = useCallback(() => {
    if (!window.fcWidget) {
      console.error('FreshChat widget not available');
      return;
    }

    const user = session?.user;
    if (!user) {
      console.error('User not available for FreshChat initialization');
      return;
    }

    try {
      // Set user properties before initialization
      window.fcWidget.user.setProperties({
        ...config?.externalData,
        externalId: user._id,
        firstName: user.name,
        email: user.email,
      });

      // Initialize FreshChat with proper configuration
      window.fcWidget.init({
        token: publicEnv.NEXT_PUBLIC_FRESH_CHAT_TOKEN!,
        host: publicEnv.NEXT_PUBLIC_FRESH_CHAT_HOST!,
        headerProperty: {
          hideChatButton: config?.hideChatButton ?? true,
        },
        tags: config?.tags ?? ['user'],
        ...(config?.siteId && { siteId: config.siteId }),
      });
    } catch (error) {
      console.error('Error initializing FreshChat:', error);
    }
  }, [session?.user, config]);

  const loadFreshChatScript = useCallback(() => {
    const user = session?.user;
    if (!user) {
      console.error('User not logged in. Cannot initialize Freshchat.');
      return Promise.reject(new Error('User not logged in'));
    }

    if (!isAvailable) {
      const error = 'FreshChat configuration is missing or invalid';
      console.warn(error);
      return Promise.reject(new Error(error));
    }

    setIsLoading(true);

    // Check if FreshChat widget is already loaded
    if (window.fcWidget) {
      initializeFreshChat();
      setIsLoading(false);
      return Promise.resolve();
    }

    const src = `${publicEnv.NEXT_PUBLIC_FRESH_CHAT_HOST}/js/widget.js`;

    return loadJS(src)
      .then(() => {
        initializeFreshChat();
      })
      .catch((err) => {
        console.error('Failed to load FreshChat script:', err);
        throw new Error('Failed to load FreshChat script');
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [session?.user, isAvailable, initializeFreshChat]);

  const openChat = useCallback(() => {
    if (!isAvailable) {
      console.warn('FreshChat is not available');
      return;
    }

    if (isLoading) {
      console.warn('FreshChat is still loading, please wait...');
      return;
    }

    if (window.fcWidget) {
      setIsLoading(true);
      try {
        window.fcWidget.open();
        setTimeout(() => {
          setIsLoading(false);
        }, 1000);
      } catch (error) {
        console.error('Error opening FreshChat:', error);
        setIsLoading(false);
      }
    } else {
      loadFreshChatScript()
        .then(() => {
          if (window.fcWidget) {
            setIsLoading(true);
            try {
              window.fcWidget.open();
              setTimeout(() => {
                setIsLoading(false);
              }, 1000);
            } catch (error) {
              console.error('Error opening FreshChat:', error);
              setIsLoading(false);
            }
          }
        })
        .catch((error) => {
          console.error('Failed to open FreshChat:', error);
        });
    }
  }, [isAvailable, isLoading, loadFreshChatScript]);

  const closeChat = useCallback(() => {
    if (window.fcWidget) {
      window.fcWidget.close();
    }
  }, []);

  return {
    isAvailable: !!isAvailable,
    openChat,
    closeChat,
    isLoading,
  };
}
